import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Card, CardContent, CardHeader } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { importStatusTitles } from 'constants.js';
import { CloseIcon, TableWithIcons, Toolbar } from 'components';
import {
  resetSnackbar,
  importFile,
  getImportStatusList,
  getErrorFile,
  downloadedErrorFile,
  getList
} from 'store/lists/actions';
import UserMessageSnackbar from 'views/UserMessaging/customModalContent/userMessageSnackbar';
import ImportFileModal from './components/ImportFileModal';
import {
  updateSortBy,
  updateSortOrder
} from 'store/helpers/tableAndToolbar/actions';
import { listHelpers } from 'store/helpers/tableAndToolbar';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  headerContainer: {
    display: 'flex',
    width: '100%'
  },
  title: {
    paddingBottom: '2px',
    width: '95%'
  },
  backButton: {
    marginBottom: '16px',
    marginLeft: '16px'
  },
  content: {
    paddingBottom: '8px'
  }
}));

const ImportStatus = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { listId } = useParams();

  const {
    isTemplateReadOnly,
  } = listHelpers();

  const importStatusList = useSelector(
    store => store.lists.importStatusList.listsData
  );
  const gettingImportStatusList = useSelector(
    store => store.lists.importStatusList.getting
  );
  const showSnackbar = useSelector(store => store.lists.showSnackbar);
  const snackbarMessage = useSelector(store => store.lists.snackbarMessage);
  const snackbarSeverity = useSelector(store => store.lists.snackbarSeverity);
  const errorFile = useSelector(store => store.lists.errorFile.file);
  const gettingErrorFile = useSelector(store => store.lists.errorFile.getting);
  const downloadingErrorFile = useSelector(
    store => store.lists.errorFile.isDownloading
  );
  const gettingErrorFileSucceeded = useSelector(
    store => store.lists.errorFile.gettingSucceeded
  );
  const currentList = useSelector(store => store.lists.subscribersList.currentList);
  const gettingSubscribers = useSelector(store => store.lists.profileList.getting);

  const [snackbarInfo, setSnackBarInfo] = useState({ show: false });
  const [openImportFileModal, setOpenImportFileModal] = useState(false);
  const [errorFileName, setErrorFileName] = useState('');

  useEffect(() => {
    if (!gettingErrorFile) {
      if (gettingErrorFileSucceeded && errorFile) {
        const url = window.URL.createObjectURL(new Blob([errorFile]));
        const link = document.createElement('a');
        link.href = url;
        link.download = errorFileName;
        link.click();
      }
      dispatch(downloadedErrorFile());
    }
  }, [gettingErrorFileSucceeded]);

  useEffect(() => {
    dispatch(resetSnackbar());
    dispatch(updateSortBy('updateTime'));
    dispatch(updateSortOrder('DESC'));
    dispatch(getList(listId));
    dispatch(getImportStatusList(listId));
  }, []);

  useEffect(() => {
    if (showSnackbar) {
      setSnackBarInfo({
        show: true,
        message: snackbarMessage,
        severity: snackbarSeverity
      });
    } else {
      setSnackBarInfo({
        show: false,
        message: ''
      });
    }
  }, [showSnackbar]);

  const getButtonsGroup = () => [
    {
      name: 'Refresh',
      action: () => dispatch(getImportStatusList(listId))
    },
    ...(!isTemplateReadOnly(currentList, gettingSubscribers) ? [{
      name: 'Import',
      action: () => onClickImport()
    }] : [])
  ];

  const onClickImport = () => {
    setOpenImportFileModal(true);
  };

  const closeImportModal = isCloseIconClicked => {
    setOpenImportFileModal(false);
    if (!isCloseIconClicked) {
      dispatch(getImportStatusList(listId));
    }
  };

  const importCsvFile = file => {
    dispatch(importFile({ listId: listId, file: file }));
  };

  const onClickLink = (name, errorFileId) => {
    setErrorFileName(name);
    errorFileId ? dispatch(getErrorFile({ errorFileId: errorFileId })) : '';
  };

  const navBack = () => {
    history.push('/lists');
  };

  const getFilteredRows = () => {
    return importStatusList.map(row => {
      let iconButton;
      switch (row.state) {
        case 'QUEUED':
          iconButton = 'AccessTime';
          break;
        case 'NOT_ACCEPTED':
          iconButton = 'CancelOutlined';
          break;
        case 'PROCESSING':
          iconButton = 'HourglassEmpty';
          break;
        case 'COMPLETED_PARTIALLY':
          iconButton = 'ReportProblemOutlined';
          break;
        case 'COMPLETED_SUCCESS':
          iconButton = 'CheckCircleOutlined';
          break;
        case 'COMPLETED_FAILED':
          iconButton = 'ErrorOutline';
          break;
      }
      let tooltipText = '';
      tooltipText = row.errorFileId ? 'Click to download' : '';
      return { ...row, icon: iconButton, tooltipContent: tooltipText};
    });
  };

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        {isTemplateReadOnly(currentList, !currentList.name) &&
          <Alert severity="info">
            You can only view this list and do not have access to
            edit. Tenant Default list can be edited only with 'ALL' property.
          </Alert>}
        <Card>
          <div className={classes.headerContainer}>
            <CardHeader
              className={classes.title}
              title="Import Status"
              titleTypographyProps={{ variant: 'h3' }}
            />
            <CloseIcon onClickClose={navBack} />
          </div>
          <CardContent>
            <Toolbar
              searchPlaceholder={'Search file name'}
              buttonsGroup={getButtonsGroup()}
              action={getImportStatusList(listId)}
              searchInputSize="small"
            />
            <TableWithIcons
              isLoading={gettingImportStatusList}
              tableTitles={importStatusTitles}
              filteredRows={getFilteredRows()}
              action={getImportStatusList(listId)}
              onClickLink={(errorFileName, errorFileId) =>
                onClickLink(errorFileName, errorFileId)
              }
              isLinkDisabled={downloadingErrorFile}
            />
          </CardContent>
          <Button
            className={classes.backButton}
            color="secondary"
            variant="contained"
            onClick={navBack}>
            Back
          </Button>
        </Card>
      </div>

      {openImportFileModal && (
        <ImportFileModal
          isOpen={openImportFileModal}
          closeImportModal={closeImportModal}
          importCsv={importCsvFile}
        />
      )}

      {showSnackbar && (
        <UserMessageSnackbar
          openSnackbar={snackbarInfo.show}
          message={snackbarInfo.message}
          severity={snackbarInfo.severity}
          onClose={() => dispatch(resetSnackbar())}
        />
      )}
    </div>
  );
};

export default ImportStatus;
